import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  DollarSign,
  Package,
  Phone,
  FileText,
  LogOut,
  Menu,
  X,
  Truck,
  CreditCard,
  BarChart3,
  Store,
  Building2,
  ShoppingBag,
  History,
  Navigation,
  Sparkles,
  Wrench,
  AlertCircle,
  Image,
  Tag,
  TrendingUp,
  Star,
  Boxes,
} from 'lucide-react';
import { signOut } from '@/services/auth';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
    } catch (error) {
      toast.error('Failed to sign out');
    }
  };

  // Role-based navigation items
  const getNavItems = () => {
    const baseItems = [
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    ];

    // Super Admin sees HQ + executive dashboards
    if (user?.role === 'super_admin') {
      baseItems.push({ path: '/hq-dashboard', icon: Building2, label: 'HQ Dashboard' });
      baseItems.push({ path: '/cfo-dashboard', icon: DollarSign, label: 'CFO Dashboard' });
      baseItems.push({ path: '/coo-dashboard', icon: BarChart3, label: 'COO Dashboard' });
      baseItems.push({ path: '/cto-dashboard', icon: Wrench, label: 'CTO Dashboard' });
    }

    // Store Manager sees Store Operations
    if (user?.role === 'store_manager') {
      baseItems.push({ path: '/store-manager', icon: Store, label: 'My Store' });
    }

    // Common items for all roles
    const commonItems = [
      { path: '/sales', icon: ShoppingCart, label: 'Sales' },
      { path: '/clients', icon: Users, label: 'Clients' },
      { path: '/expenses', icon: DollarSign, label: 'Expenses' },
      { path: '/inventory', icon: Package, label: 'Inventory' },
      { path: '/inventory-tracker', icon: Boxes, label: 'Stock Tracker' },
      { path: '/suppliers', icon: Truck, label: 'Suppliers' },
      { path: '/payments', icon: CreditCard, label: 'Payments' },
      { path: '/analytics', icon: BarChart3, label: 'Analytics' },
      { path: '/calls', icon: Phone, label: 'Call Assistant' },
      { path: '/reports', icon: FileText, label: 'Reports' },
      { path: '/product-photos', icon: Image, label: 'Product Photos' },
      { path: '/price-settings', icon: Tag, label: 'Price Settings' },
      { path: '/staff-requests', icon: Wrench, label: 'Staff Requests' },
      { path: '/issue-reports', icon: AlertCircle, label: 'Issue Reports' },
    ];

    // Admin + Super Admin extras
    if (user?.role === 'super_admin' || user?.role === 'admin') {
      commonItems.push({ path: '/logistics', icon: Navigation, label: 'Logistics & Fleet' });
      commonItems.push({ path: '/orders', icon: ShoppingBag, label: 'Customer Orders' });
      commonItems.push({ path: '/delivery-earnings', icon: TrendingUp, label: 'Delivery Earnings' });
      commonItems.push({ path: '/memberships', icon: Star, label: 'Plus Members' });
      commonItems.push({ path: '/supply-plans', icon: Truck, label: 'Supply Plans' });
      commonItems.push({ path: '/audit-log', icon: History, label: 'Audit Log' });
      commonItems.push({ path: '/sal-chat', icon: Sparkles, label: 'Chat with Sal' });
      commonItems.push({ path: '/sal-insights', icon: TrendingUp, label: 'Sal Insights' });
    }

    return [...baseItems, ...commonItems];
  };

  const navItems = getNavItems();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#070711] via-[#0d0d1f] to-[#111827] flex">

      {/* ─── Desktop Sidebar ─── */}
      <aside className="hidden lg:flex flex-col w-60 min-h-screen sticky top-0 shrink-0 bg-white/[0.04] backdrop-blur-xl border-r border-white/10">

        {/* Logo */}
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <span className="text-2xl leading-none">🧅</span>
            <div>
              <p className="font-bold text-white text-[15px] leading-tight">Onion Trade</p>
              <p className="text-[11px] text-white/40 mt-0.5">Sales Intelligence</p>
            </div>
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 py-4 px-2.5 space-y-0.5 overflow-y-auto scrollbar-hide">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-violet-500/30 to-blue-500/20 text-white border border-violet-500/25 shadow-lg shadow-violet-500/10'
                    : 'text-white/50 hover:text-white/90 hover:bg-white/[0.07]'
                }`}
              >
                <Icon size={16} className={isActive ? 'text-violet-400' : ''} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User card + sign out */}
        <div className="p-3 border-t border-white/10">
          <div className="mb-2.5 px-2 py-2.5 rounded-xl bg-gradient-to-br from-violet-500/20 to-blue-500/15 border border-violet-500/20">
            <p className="text-[13px] font-semibold text-white truncate">{user?.name}</p>
            <p className="text-[11px] text-white/40 truncate mt-0.5">{user?.email}</p>
            <span className="inline-flex mt-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-violet-500/30 text-violet-300 border border-violet-500/30">
              {user?.role}
            </span>
          </div>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-all text-[13px] font-medium"
          >
            <LogOut size={15} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* ─── Mobile Header ─── */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#070711]/90 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="text-xl leading-none">🧅</span>
          <span className="font-bold text-white text-[15px]">Onion Trade</span>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
        >
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* ─── Mobile Menu Drawer ─── */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div
            className="bg-[#0d0d1f] border-r border-white/10 w-64 min-h-screen pt-16"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-4 border-b border-white/10">
              <p className="text-sm font-semibold text-white">{user?.name}</p>
              <p className="text-xs text-white/40 mt-0.5">{user?.email}</p>
              <span className="inline-flex mt-2 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-500/30 text-violet-300 border border-violet-500/30">
                {user?.role}
              </span>
            </div>
            <nav className="p-3 space-y-0.5">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all ${
                      isActive
                        ? 'bg-gradient-to-r from-violet-500/30 to-blue-500/20 text-white border border-violet-500/25'
                        : 'text-white/50 hover:text-white hover:bg-white/[0.07]'
                    }`}
                  >
                    <Icon size={16} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="p-3 border-t border-white/10">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-all text-[13px] font-medium"
              >
                <LogOut size={15} />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Main content ─── */}
      <main className="flex-1 overflow-auto min-h-screen pt-[52px] lg:pt-0">
        <div className="lg:p-8 p-4">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
